/**
 * This is an extension to Leaflet allowing to display TomTom base raster map layers.
 * It is based on the raster L.TileLayer implementation.
 * The options that can be passed on creation of this object are described for the .tomTomLayer() function.
 *
 * @example
 *  var tomtomLayer = L.tomTomLayer({key: "<Your API key>"});
 *
 * @module Map
 * @class TomTomLayer
 * @extends L.TileLayer
 */
/**
 * Instantiates a new instance of the TomTom base raster map layer.
 *
 * @method tomTomLayer
 * @param {Object} [options] All the options that Leaflet allows, as described here:
 *     http://leafletjs.com/reference.html#tilelayer-options Additional options, which are TomTom specific, are
 *     described below.
 * @param {String} [options.key] This is an API key that can be obtained from http://developer.tomtom.com When the
 *     key is passed on the map's initialization, it is set globally for all the services. It can be overwritten with
 *     the tomtom.key method globally or per service  with tomtom.&lt;service_name&gt;.key functions. It is also
 *     possible to pass a specific key while working with instances of the services.
 * @param {String} [options.style] The base map style to be used. Should be set to one of the following values:
 *     &lt;'main', 'night'&gt;. By default, the _main_ style is used.
 * @param {String} [options.layer] The base map's layer to be used. Should be set to one of the following values:
 *     &lt;'basic', 'hybrid', 'labels'&gt;. By default, the _basic_ layer is used.
 */
define('display/layers/tileLayer',['leaflet', 'security', 'http', 'endpoints', 'display/layers/mapTheme', './animator', './optionsComplementor'],
    function(L, security, http, endpoints, mapTheme, animator, optionsComplementor) {
        'use strict';

        function use512Tiles(detectRetina, style, layer) {
            return detectRetina && L.Browser.retina && style === 'main' && ['basic', 'labels'].indexOf(layer) > -1;
        }

        function tileSizeEstimator(options) {
            return use512Tiles(options.detectRetina, options.style, options.layer) ? 512 : 256;
        }

        L.TomTomLayer = L.TileLayer.extend({
            options: {
                attribution: '',
                minZoom: 2,
                maxZoom: 18,
                subdomains: 'abcd',
                reuseTiles: true,
                detectRetina: false
            },
            name: 'raster',
            initialize: function(options) {
                var key, theme;
                options = L.setOptions(this, options);
                if (options.bounds) {
                    options.bounds = L.latLngBounds(options.bounds);
                }
                security.setKey(options.key);
                key = security.getKey('services/display');
                options.accessToken = key;
                /* if 512 tiles are working uncomment the following line
                 this._url = http.protocol() + '://'+ endpoints.tileLayer +
                 '?tileSize={tileSize}' + (key ? '&key={accessToken}' : '');
                 */
                this._url = http.protocol() + '://' + endpoints.tileLayer + (key ? '?key={accessToken}' : '');
                theme = mapTheme.getTheme([options]);
                options.style = theme.style;
                options.layer = theme.layer;
                optionsComplementor.fillInSubdomainsOptions(options);
                optionsComplementor.fillInTiles512Options(options, tileSizeEstimator);
            },
            onAdd: function(map) {
                this._map = map;
                L.TileLayer.prototype.onAdd.call(this, map);
                if (this._animated) {
                    this.on('load', animator.loadAnimationFactory(this));
                }
                this._updateOpacity();
                this.bringToBack();
            },
            updateOptions: function(options) {
                var theme = mapTheme.getTheme([this.options, options]);
                this.options.style = theme.style;
                this.options.layer = theme.layer;
                this.redraw();
            }
        });

        L.tomTomLayer = function(options) {
            return new L.TomTomLayer(options);
        };

        return L.tomTomLayer;
    });

